import Section from "../common/section";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { PlusIcon, MinusIcon } from "@heroicons/react/20/solid";
import { getThemeClass } from "~/styles/styles";
import { forwardRef, LegacyRef } from "react";

export type FaqItem = {
  question: string;
  answer: string | string[] | JSX.Element;
};

export type FaqProps = {
  items: FaqItem[];
  title?: string;
  bgClass?: string;
  theme?: string;
  customPadding?: boolean; // padding to fit with testimonial section
};

const FaqSection = (props: FaqProps, ref: LegacyRef<HTMLElement> | undefined) => {
  const theme = props.theme
    ? getThemeClass(props.theme)
    : getThemeClass("light");
  const darkTheme = props.theme?.includes("dark");
  const titleColor = darkTheme ? "text-white" : "text-purpleblue";
  const iconColor = darkTheme ? "fill-white" : "fill-purpleblue";

  return (
    <Section className={`${theme} ${props.bgClass ?? ""} flex`} ref={ref}>
      <div
        className={`m-auto max-w-6xl flex-1 px-8 py-16 ml:px-16 lg:py-20 ${props.customPadding ? "pb-48 xs:pb-48 sm:pb-48 md:py-16" : ""}`}
      >
        <h3
          className={`text-purpleblu text-4xl leading-10 tracking-tight ${titleColor}`}
        >
          {props.title ?? "FAQs"}
        </h3>
        <div className={`mt-6 divide-y`}>
          {props.items.map((faq, i) => (
            <Disclosure
              as="div"
              className={`py-3 ${darkTheme ? "divide-white/60 hover:bg-black/10" : "divide-grey-85/40 hover:bg-grey-65/5"}`}
              defaultOpen={false}
              key={i}
            >
              <dt>
                <DisclosureButton className="group flex w-full items-start justify-between text-left">
                  <h4
                    className={`text-base font-semibold leading-7 md:text-lg xl:text-lg`}
                  >
                    {faq.question}
                  </h4>
                  <span className="ml-6 flex h-7 items-center">
                    <PlusIcon
                      className={`size-6 ${iconColor} group-data-[open]:hidden`}
                    />
                    <MinusIcon
                      className={`hidden size-6 ${iconColor} group-data-[open]:block`}
                    />
                  </span>
                </DisclosureButton>
              </dt>

              <DisclosurePanel as="dd" className="py-2">
                <div className={`text-resp-base flex flex-col gap-2 leading-7`}>
                  {Array.isArray(faq.answer)
                    ? faq.answer.map((line, i) => (
                        <p key={i} className="mt-1 md:mt-2">
                          {line}
                        </p>
                      ))
                    : faq.answer}
                </div>
              </DisclosurePanel>
            </Disclosure>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default forwardRef(FaqSection);
